import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Clip } from '../../types';
import { uploadUserClip, getTempVaultClips, saveTempVaultClip, MAX_TEMP_CLIPS } from '../../services/curatorService';
import {
  CheckCircle2,
  Play,
  Pause,
  Film,
  Repeat,
  RotateCcw,
  RotateCw,
  Upload,
  ShieldCheck,
  AlertCircle,
  FileVideo,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { useTokens } from '../../context/TokenContext';

interface ClipCuratorProps {
  currentClip?: Clip | null;
  userName?: string;
  onClipSelect: (clip: Clip) => void;
  onStartSeparation?: (clip: Clip) => void;
  onSendToSplit?: (clip: Clip) => void;
  onSendToShare?: (clip: Clip) => void;
}

export const ClipCurator: React.FC<ClipCuratorProps> = ({
  currentClip,
  userName,
  onClipSelect,
  onStartSeparation,
  onSendToSplit,
  onSendToShare
}) => {
  const { balance, isPro, openPurchaseModal } = useTokens();

  // File Upload & Preview State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadStatus, setLoadStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState<boolean>(false);

  // Legal Compliance: Mandatory Rights Warranty Checkbox
  const [rightsAffirmed, setRightsAffirmed] = useState<boolean>(false);

  // Playback & Duration state from Native Video Player
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  // Playback controls overlay & container state
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showOverlayControls, setShowOverlayControls] = useState<boolean>(true);
  const overlayTimeoutRef = useRef<number | null>(null);

  // Trim range state (Interactive Handles, default 15s window: 5s <= span <= 60s)
  const [anchorTime, setAnchorTime] = useState<number>(0);
  const [inPoint, setInPoint] = useState<number>(0);
  const [outPoint, setOutPoint] = useState<number>(15.0);
  const [isReviewing, setIsReviewing] = useState<boolean>(false);

  // Loop Review content ad eternum (default ON)
  const [isLooping, setIsLooping] = useState<boolean>(true);
  const isLoopingRef = useRef<boolean>(true);

  // Clip name for SoulCapture
  const [clipName, setClipName] = useState<string>('');

  // Static Zoom Bounds to prevent recalculation when dragging handles in review mode
  const [isZoomed, setIsZoomed] = useState<boolean>(false);
  const [zoomBounds, setZoomBounds] = useState<{ start: number; end: number } | null>(null);

  // Track if handles have been manipulated since last review trigger
  const [handlesManipulated, setHandlesManipulated] = useState<boolean>(false);
  const handlesManipulatedRef = useRef<boolean>(false);
  const lastReviewedRangeRef = useRef<{ inPoint: number; outPoint: number } | null>(null);

  // Handles visibility on timeline
  const [showHandles, setShowHandles] = useState<boolean>(false);
  const showHandlesRef = useRef<boolean>(false);

  // Mutable refs for 60fps watcher & drag tracking
  const isReviewingRef = useRef<boolean>(false);
  const inPointRef = useRef<number>(0);
  const outPointRef = useRef<number>(15.0);
  const anchorTimeRef = useRef<number>(0);
  const suppressAnchorUntilRef = useRef<number>(0);

  // Dragging state refs
  const draggingRef = useRef<'in' | 'out' | null>(null);
  const dragViewStartRef = useRef<number>(0);
  const dragViewSpanRef = useRef<number>(1);

  // Video and Track DOM refs
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const isCheckingStatusRef = useRef<boolean>(false);

  // Fullscreen change listener
  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Format seconds to mm:ss.s
  const formatTime = (secs: number) => {
    if (!Number.isFinite(secs) || secs < 0) return '0:00.0';
    const totalSecs = Math.floor(secs);
    const ms = Math.floor((secs % 1) * 10);
    const s = totalSecs % 60;
    const m = Math.floor((totalSecs % 3600) / 60);
    const h = Math.floor(totalSecs / 3600);
    const sStr = s < 10 ? `0${s}` : `${s}`;

    if (h > 0) {
      const mStr = m < 10 ? `0${m}` : `${m}`;
      return `${h}:${mStr}:${sStr}.${ms}`;
    }
    return `${m}:${sStr}.${ms}`;
  };

  const safeDuration = duration > 0 ? duration : 60;
  const totalSpan = Math.max(0.1, Math.round((outPoint - inPoint) * 10) / 10);
  // Gate requirement: 5s <= totalSpan <= 60s
  const isSpanValid = totalSpan >= 5.0 && totalSpan <= 60.0;

  // Keep refs in sync for 60fps watcher
  useEffect(() => {
    inPointRef.current = inPoint;
    outPointRef.current = outPoint;
    anchorTimeRef.current = anchorTime;
    isReviewingRef.current = isReviewing;
    isLoopingRef.current = isLooping;
  }, [inPoint, outPoint, anchorTime, isReviewing, isLooping]);

  // Handle Video File Selection
  const handleFileChange = (file: File) => {
    setErrorMessage(null);

    // Validate size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      setErrorMessage('Video exceeds the 100MB upload limit. Please select a shorter or compressed clip.');
      return;
    }

    // Validate type
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    const isVideo = validTypes.some(t => file.type.includes(t)) || file.name.endsWith('.mp4') || file.name.endsWith('.webm');
    if (!isVideo) {
      setErrorMessage('Please select a valid MP4 or WebM video file.');
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    const newUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(newUrl);
    setClipName(file.name.replace(/\.[^/.]+$/, '').slice(0, 30));
    setLoadStatus(`Loaded: ${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`);

    setIsReviewing(false);
    isReviewingRef.current = false;
    setIsZoomed(false);
    setZoomBounds(null);
    setShowHandles(false);
    showHandlesRef.current = false;
    handlesManipulatedRef.current = false;
    setHandlesManipulated(false);
    lastReviewedRangeRef.current = null;
    setCurrentTime(0);
    setAnchorTime(0);
    setInPoint(0);
    setOutPoint(15);
  };

  // Video Element Metadata Loaded
  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    const dur = videoRef.current.duration;
    if (dur && Number.isFinite(dur)) {
      setDuration(dur);
      setOutPoint(Math.min(dur, 15.0));
      setLoadStatus(`Video Ready (${formatTime(dur)})`);
      setShowHandles(true);
      showHandlesRef.current = true;
    }
  };

  // 60FPS RequestAnimationFrame Loop:
  // Strictly enforces boundary stop / infinite loop and smooth playhead tracking
  useEffect(() => {
    let rafId: number;

    const tick = () => {
      const vid = videoRef.current;
      if (vid && !vid.paused) {
        const now = vid.currentTime;
        if (Number.isFinite(now)) {
          setCurrentTime(now);

          // BOUNDARY ENFORCEMENT & LOOP REVIEW:
          if (isReviewingRef.current && outPointRef.current > 0 && now >= outPointRef.current) {
            if (isLoopingRef.current) {
              suppressAnchorUntilRef.current = Date.now() + 1500;
              vid.currentTime = inPointRef.current;
              vid.play().catch(() => {});
              setCurrentTime(inPointRef.current);
            } else {
              suppressAnchorUntilRef.current = Date.now() + 1500;
              isReviewingRef.current = false;
              setIsReviewing(false);
              setIsPlaying(false);
              vid.pause();
              vid.currentTime = inPointRef.current;
              setCurrentTime(inPointRef.current);
            }
          }
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Timeline viewport calculations
  const zoomStart = (isZoomed && zoomBounds) ? zoomBounds.start : 0;
  const zoomEnd = (isZoomed && zoomBounds) ? zoomBounds.end : safeDuration;
  const viewSpan = Math.max(0.1, zoomEnd - zoomStart);

  const timeToPercent = (t: number) => {
    return Math.max(0, Math.min(100, ((t - zoomStart) / viewSpan) * 100));
  };

  // Dragging Handles with 5s <= span <= 60s Gating
  const handleStartDrag = (handle: 'in' | 'out', e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
      setIsPlaying(false);
    }

    draggingRef.current = handle;
    dragViewStartRef.current = zoomStart;
    dragViewSpanRef.current = viewSpan;

    handlesManipulatedRef.current = true;
    setHandlesManipulated(true);

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!draggingRef.current || !trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const clickX = Math.max(0, Math.min(rect.width, moveEvent.clientX - rect.left));
      const pct = clickX / rect.width;
      const rawTime = dragViewStartRef.current + (pct * dragViewSpanRef.current);
      const boundedTime = Math.max(0, Math.min(safeDuration, Math.round(rawTime * 10) / 10));

      if (draggingRef.current === 'in') {
        const currentOut = outPointRef.current;
        const clampedIn = Math.min(boundedTime, currentOut - 5.0);
        const validIn = Math.max(0, Math.max(clampedIn, currentOut - 60.0));
        setInPoint(validIn);
        inPointRef.current = validIn;
        if (videoRef.current) {
          videoRef.current.currentTime = validIn;
          setCurrentTime(validIn);
        }
      } else {
        const currentIn = inPointRef.current;
        const clampedOut = Math.max(boundedTime, currentIn + 5.0);
        const validOut = Math.min(safeDuration, Math.min(clampedOut, currentIn + 60.0));
        setOutPoint(validOut);
        outPointRef.current = validOut;
        if (videoRef.current) {
          videoRef.current.currentTime = validOut;
          setCurrentTime(validOut);
        }
      }
    };

    const onMouseUp = () => {
      draggingRef.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // Automatically sets handles around the current playhead (15s default span: -10s, +5s, min 5s)
  const placeHandlesAroundPlayhead = useCallback((t: number) => {
    if (isReviewingRef.current || draggingRef.current) return;
    const dur = duration > 0 ? duration : safeDuration;
    const anchor = Math.max(0, Math.min(dur, t));
    setAnchorTime(anchor);
    anchorTimeRef.current = anchor;

    let newIn = Math.max(0, Math.round((anchor - 10) * 10) / 10);
    let newOut = Math.min(dur, Math.round((anchor + 5) * 10) / 10);

    // Ensure minimum span of 5.0s
    if (newOut - newIn < 5.0) {
      if (newIn === 0) {
        newOut = Math.min(dur, newIn + 5.0);
      } else if (newOut === dur) {
        newIn = Math.max(0, newOut - 5.0);
      } else {
        const diff = 5.0 - (newOut - newIn);
        newOut = Math.min(dur, newOut + diff / 2);
        newIn = Math.max(0, newIn - diff / 2);
      }
    }

    setInPoint(newIn);
    setOutPoint(newOut);
    inPointRef.current = newIn;
    outPointRef.current = newOut;
    setShowHandles(true);
    showHandlesRef.current = true;
    setHandlesManipulated(false);
    handlesManipulatedRef.current = false;
  }, [duration, safeDuration]);

  // Video container mouse movement handlers for overlay fade
  const handleMouseMoveContainer = () => {
    setShowOverlayControls(true);
    if (overlayTimeoutRef.current) {
      window.clearTimeout(overlayTimeoutRef.current);
    }
    if (isPlaying) {
      overlayTimeoutRef.current = window.setTimeout(() => {
        setShowOverlayControls(false);
      }, 2500);
    }
  };

  const handleMouseLeaveContainer = () => {
    if (isPlaying) {
      setShowOverlayControls(false);
    }
  };

  const handleJumpTime = (delta: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    const target = Math.max(0, Math.min(safeDuration, videoRef.current.currentTime + delta));
    videoRef.current.currentTime = target;
    setCurrentTime(target);
    if (!isPlaying && !isReviewingRef.current) {
      placeHandlesAroundPlayhead(target);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      const nextMuted = !videoRef.current.muted;
      videoRef.current.muted = nextMuted;
      setIsMuted(nextMuted);
    }
  };

  const handleToggleFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoContainerRef.current) return;
    if (!document.fullscreenElement) {
      videoContainerRef.current.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  const handleSetInFromPlayhead = (e: React.MouseEvent) => {
    e.stopPropagation();
    const t = Math.max(0, Math.min(safeDuration, currentTime));
    const currentOut = outPointRef.current;
    const clampedIn = Math.min(t, currentOut - 5.0);
    const validIn = Math.max(0, Math.max(clampedIn, currentOut - 60.0));
    const rounded = Math.round(validIn * 10) / 10;
    setInPoint(rounded);
    inPointRef.current = rounded;
    setShowHandles(true);
    showHandlesRef.current = true;
    setHandlesManipulated(true);
    handlesManipulatedRef.current = true;
  };

  const handleSetOutFromPlayhead = (e: React.MouseEvent) => {
    e.stopPropagation();
    const t = Math.max(0, Math.min(safeDuration, currentTime));
    const currentIn = inPointRef.current;
    const clampedOut = Math.max(t, currentIn + 5.0);
    const validOut = Math.min(safeDuration, Math.min(clampedOut, currentIn + 60.0));
    const rounded = Math.round(validOut * 10) / 10;
    setOutPoint(rounded);
    outPointRef.current = rounded;
    setShowHandles(true);
    showHandlesRef.current = true;
    setHandlesManipulated(true);
    handlesManipulatedRef.current = true;
  };

  // Click on Timeline Track to Scrub Playhead
  const handleMouseDownTrack = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!previewUrl || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const clickX = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const pct = clickX / rect.width;
    const targetTime = Math.max(0, Math.min(safeDuration, zoomStart + (pct * viewSpan)));

    if (!showHandles) {
      setShowHandles(true);
      showHandlesRef.current = true;
    }

    if (videoRef.current) {
      videoRef.current.currentTime = targetTime;
      setCurrentTime(targetTime);
      if (!isPlaying && !isReviewingRef.current) {
        placeHandlesAroundPlayhead(targetTime);
      }
    }
  };

  // Play / Pause Toggle for Full Video
  const handleTogglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
      setShowHandles(true);
      showHandlesRef.current = true;
      if (!isReviewingRef.current) {
        placeHandlesAroundPlayhead(videoRef.current.currentTime);
      }
    } else {
      setIsReviewing(false);
      isReviewingRef.current = false;
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  // Review Clip: Plays strictly from inPoint to outPoint and zooms timeline around selection
  const handleToggleReview = () => {
    if (!videoRef.current || !previewUrl) return;

    if (isReviewing && isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
      return;
    }

    // Zoom timeline around handles with 20% contextual padding (min 1s, max 5s)
    const span = outPoint - inPoint;
    const pad = Math.max(1.0, Math.min(5.0, span * 0.2));
    const zStart = Math.max(0, Math.round((inPoint - pad) * 10) / 10);
    const zEnd = Math.min(safeDuration, Math.round((outPoint + pad) * 10) / 10);
    setZoomBounds({ start: zStart, end: zEnd });
    setIsZoomed(true);

    const isCurrentRange = lastReviewedRangeRef.current &&
      lastReviewedRangeRef.current.inPoint === inPoint &&
      lastReviewedRangeRef.current.outPoint === outPoint;

    const canResume = isReviewing && !isPlaying && !handlesManipulated && isCurrentRange &&
      currentTime >= inPoint && currentTime < (outPoint - 0.25);

    if (canResume) {
      setIsPlaying(true);
      videoRef.current.play().catch(() => {});
      return;
    }

    setIsReviewing(true);
    isReviewingRef.current = true;
    setIsPlaying(true);

    lastReviewedRangeRef.current = { inPoint, outPoint };
    handlesManipulatedRef.current = false;
    setHandlesManipulated(false);

    videoRef.current.currentTime = inPoint;
    setCurrentTime(inPoint);
    videoRef.current.play().catch(() => {});
  };

  // Reset Trim to default around current playhead
  const handleReset = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsPlaying(false);
    setIsReviewing(false);
    isReviewingRef.current = false;
    setIsZoomed(false);
    setZoomBounds(null);
    handlesManipulatedRef.current = false;
    setHandlesManipulated(false);
    lastReviewedRangeRef.current = null;

    const resetIn = Math.max(0, Math.round((anchorTime - 10) * 10) / 10);
    const resetOut = Math.min(safeDuration, Math.round((anchorTime + 5) * 10) / 10);
    setInPoint(resetIn);
    setOutPoint(resetOut);
    inPointRef.current = resetIn;
    outPointRef.current = resetOut;

    if (videoRef.current) {
      videoRef.current.currentTime = anchorTime;
      setCurrentTime(anchorTime);
    }
  };

  // SoulCapture: Instantly navigates to SoulStudio where the 3-op loader tracks registration, R2 transfer & GPU processing
  const handleUploadAndCapture = () => {
    if (!selectedFile || !rightsAffirmed || !isSpanValid || !clipName.trim()) return;

    // Token Balance Pre-Check
    if (userName && !isPro && balance < 1) {
      openPurchaseModal('packs');
      return;
    }

    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }

    const now = Date.now();
    const clipId = `clip-upload-${now.toString(36)}-${Math.random().toString(36).substring(2, 7)}`;
    const targetDuration = Math.max(1.0, Math.round((outPoint - inPoint) * 10) / 10);

    let localPosterUrl = `/api/media/clips/${clipId}.jpg`;
    if (videoRef.current) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth || 1280;
        canvas.height = videoRef.current.videoHeight || 720;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
          localPosterUrl = canvas.toDataURL('image/jpeg', 0.6);
        }
      } catch (err) {
        console.warn('Could not extract local thumbnail', err);
      }
    }

    const capturedClip: Clip = {
      id: clipId,
      title: clipName.trim() || `User Upload ${new Date(now).toISOString().slice(0, 10)}`,
      sourceUrl: previewUrl || `/api/media/clips/${clipId}.mp4`,
      videoUrl: previewUrl || `/api/media/clips/${clipId}.mp4`,
      audioUrl: `/api/media/stems/${clipId}/bgm.mp3`,
      vocalsUrl: `/api/media/stems/${clipId}/vocals.mp3`,
      posterUrl: localPosterUrl,
      duration: targetDuration,
      trimStart: 0,
      trimEnd: targetDuration,
      rawTrimStart: inPoint,
      rawTrimEnd: outPoint,
      pendingFile: selectedFile,
      playbackMode: 'html5',
      hasStems: false
    };

    // 1. Immediately persist optimistic clip to local Vault so it appears in the Vault drawer
    saveTempVaultClip(capturedClip);

    // Ensure the SoulVault drawer is closed when transitioning to SoulStudio
    try {
      sessionStorage.setItem('soulsync_vault_open', 'false');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('soulsync:close_vault'));
      }
    } catch (_) {}

    // Purge lingering browser deep link query/hash so stale deep link handlers don't overwrite the new capture
    try {
      if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch (_) {}

    // 2. Immediately hand off clip to SoulStudio (instantly navigates to the soulsync page)
    if (onSendToSplit) {
      onSendToSplit(capturedClip);
    } else {
      onClipSelect(capturedClip);
    }

    // Reset Curator form state for clean subsequent imports
    setSelectedFile(null);
    setPreviewUrl(null);
    setClipName('');
    setRightsAffirmed(false);
    setErrorMessage(null);
    setLoadStatus(null);
  };

  // Timeline Waveform Simulation Bars
  const inPointPercent = timeToPercent(inPoint);
  const outPointPercent = timeToPercent(outPoint);
  const playheadPercent = timeToPercent(currentTime);

  const waveformBars = React.useMemo(() => {
    const bars = [];
    const count = 75;
    for (let i = 0; i < count; i++) {
      const barTime = zoomStart + (i / count) * viewSpan;
      const isInsideTrim = barTime >= inPoint && barTime <= outPoint;
      const isPast = barTime <= currentTime;
      const pseudoHeight = 25 + Math.sin(i * 0.45) * 20 + Math.cos(i * 0.9) * 25 + (i % 5) * 6;
      bars.push({
        heightPercent: Math.max(15, Math.min(100, pseudoHeight)),
        isInsideTrim,
        isPast
      });
    }
    return bars;
  }, [inPoint, outPoint, currentTime, zoomStart, viewSpan]);

  return (
    <div className="curator-container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '1rem' }}>
      
      {/* Header & Tool Posture */}
      <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0, color: '#ffffff', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileVideo size={24} color="var(--accent-purple, #a855f7)" />
            SoulCapture <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--accent-coral, #ff6b6b)', padding: '2px 8px', borderRadius: '4px', background: 'rgba(255, 107, 107, 0.12)' }}>UGC Dubbing Studio</span>
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.84rem', color: 'var(--text-muted)' }}>
            Bring Your Own Content: Upload an MP4 or WebM video you own or have permission to dub.
          </p>
        </div>

        {/* Upload Action Button / Input Trigger */}
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/webm"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileChange(file);
          }}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="btn-primary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '0.55rem 1.25rem',
            fontSize: '0.88rem',
            fontWeight: 700
          }}
        >
          <Upload size={16} />
          <span>{selectedFile ? 'Choose Another Video' : 'Upload Video File'}</span>
        </button>
      </div>

      {/* Error / Status Notices */}
      {errorMessage && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.65rem 1rem',
          borderRadius: 'var(--radius-md, 8px)',
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.4)',
          color: '#fca5a5',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.86rem'
        }}>
          <AlertCircle size={16} />
          <span>{errorMessage}</span>
        </div>
      )}

      {loadStatus && !errorMessage && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.55rem 1rem',
          borderRadius: 'var(--radius-md, 8px)',
          background: 'rgba(16, 185, 129, 0.12)',
          border: '1px solid var(--accent-emerald, #10b981)',
          color: '#6ee7b7',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.85rem'
        }}>
          <CheckCircle2 size={16} />
          <span>{loadStatus}</span>
        </div>
      )}

      {/* Main Video Viewport & Dropzone */}
      <div className="glass-panel" style={{ padding: '1.25rem', borderRadius: 'var(--radius-xl, 16px)' }}>
        <div
          ref={videoContainerRef}
          onMouseMove={handleMouseMoveContainer}
          onMouseLeave={handleMouseLeaveContainer}
          onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
          onDragLeave={() => setIsDraggingFile(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingFile(false);
            const file = e.dataTransfer.files?.[0];
            if (file) handleFileChange(file);
          }}
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            maxHeight: '460px',
            borderRadius: 'var(--radius-lg, 12px)',
            overflow: 'hidden',
            background: '#04060e',
            marginBottom: '1.25rem',
            border: isDraggingFile ? '2px dashed var(--accent-purple, #a855f7)' : '1px solid var(--border-glass, rgba(255,255,255,0.1))',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none'
          }}
        >
          {!previewUrl ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                textAlign: 'center',
                padding: '2rem',
                cursor: 'pointer',
                background: 'radial-gradient(circle at center, #0f172a 0%, #04060e 100%)'
              }}
            >
              <div
                style={{
                  width: '68px',
                  height: '68px',
                  borderRadius: '50%',
                  background: 'rgba(168, 85, 247, 0.15)',
                  border: '1px solid rgba(168, 85, 247, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#c084fc',
                  boxShadow: '0 0 25px rgba(168, 85, 247, 0.25)'
                }}
              >
                <Upload size={32} />
              </div>
              <div>
                <h2 style={{ margin: '0 0 6px 0', fontSize: '1.3rem', fontWeight: 800, color: '#ffffff' }}>
                  Select or Drag & Drop a Video Scene
                </h2>
                <p style={{ margin: 0, fontSize: '0.88rem', color: 'var(--text-muted)', maxWidth: '420px', lineHeight: 1.5 }}>
                  Support for MP4 and WebM (up to 100MB, ≤60 seconds recommended). Stored privately on Cloudflare R2 at your direction.
                </p>
              </div>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                src={previewUrl}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={() => {
                  if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
                }}
                onPlay={() => setIsPlaying(true)}
                onPause={() => {
                  setIsPlaying(false);
                  if (!isReviewingRef.current && videoRef.current) {
                    placeHandlesAroundPlayhead(videoRef.current.currentTime);
                  }
                }}
                onClick={handleTogglePlay}
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'pointer' }}
              />

              {/* Floating HUD: Range & Position (Top-Right) */}
              <div
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '14px',
                  background: 'rgba(0, 0, 0, 0.85)',
                  backdropFilter: 'blur(8px)',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontFamily: 'var(--font-mono, monospace)',
                  fontSize: '0.82rem',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  pointerEvents: 'none',
                  zIndex: 15
                }}
              >
                {isReviewing ? (
                  <span style={{ color: '#6ee7b7' }}>
                    REVIEW: {formatTime(currentTime)} / {formatTime(outPoint)} {isLooping ? '🔁' : ''}
                  </span>
                ) : (
                  <span>
                    {formatTime(currentTime)} / {formatTime(safeDuration)}
                  </span>
                )}
              </div>

              {/* Center Play/Pause Overlay Button */}
              {(!isPlaying || showOverlayControls) && (
                <button
                  type="button"
                  onClick={handleTogglePlay}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    margin: 'auto',
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: isPlaying ? 'rgba(0, 0, 0, 0.55)' : 'rgba(168, 85, 247, 0.88)',
                    border: '1px solid rgba(255, 255, 255, 0.35)',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 0 25px rgba(168, 85, 247, 0.45)',
                    transition: 'all 200ms ease',
                    opacity: isPlaying ? (showOverlayControls ? 0.85 : 0) : 1,
                    pointerEvents: 'auto',
                    zIndex: 16
                  }}
                  title={isPlaying ? 'Pause Video' : 'Play Video'}
                >
                  {isPlaying ? <Pause size={28} /> : <Play size={28} style={{ marginLeft: '4px' }} />}
                </button>
              )}

              {/* Floating Bottom Transport Bar Overlay */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '14px',
                  right: '14px',
                  background: 'rgba(8, 11, 24, 0.85)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '10px',
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  zIndex: 20,
                  opacity: (!isPlaying || showOverlayControls) ? 1 : 0,
                  transition: 'opacity 250ms ease',
                  pointerEvents: (!isPlaying || showOverlayControls) ? 'auto' : 'none',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.6)'
                }}
              >
                {/* Left Controls: Play/Pause, Jump -5s/+5s, Timecode */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={handleTogglePlay}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      color: '#ffffff',
                      cursor: 'pointer',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? <Pause size={15} /> : <Play size={15} style={{ marginLeft: '2px' }} />}
                  </button>

                  <button
                    type="button"
                    onClick={(e) => handleJumpTime(-5, e)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.8)',
                      cursor: 'pointer',
                      padding: '4px 6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      fontSize: '0.74rem',
                      fontFamily: 'var(--font-mono, monospace)'
                    }}
                    title="Jump -5 seconds"
                  >
                    <RotateCcw size={13} />
                    <span>-5s</span>
                  </button>

                  <button
                    type="button"
                    onClick={(e) => handleJumpTime(5, e)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.8)',
                      cursor: 'pointer',
                      padding: '4px 6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      fontSize: '0.74rem',
                      fontFamily: 'var(--font-mono, monospace)'
                    }}
                    title="Jump +5 seconds"
                  >
                    <RotateCw size={13} />
                    <span>+5s</span>
                  </button>

                  <div style={{
                    fontFamily: 'var(--font-mono, monospace)',
                    fontSize: '0.78rem',
                    color: '#cbd5e1',
                    marginLeft: '4px'
                  }}>
                    {formatTime(currentTime)} <span style={{ color: 'rgba(255,255,255,0.4)' }}>/</span> {formatTime(safeDuration)}
                  </div>
                </div>

                {/* Right Controls: Set In/Out, Mute, Fullscreen */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={handleSetInFromPlayhead}
                    style={{
                      background: 'rgba(225, 29, 72, 0.2)',
                      border: '1px solid rgba(225, 29, 72, 0.5)',
                      borderRadius: '5px',
                      color: '#fca5a5',
                      cursor: 'pointer',
                      padding: '3px 8px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      fontFamily: 'var(--font-mono, monospace)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}
                    title="Set In-Point at current playhead"
                  >
                    <span>[ IN</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleSetOutFromPlayhead}
                    style={{
                      background: 'rgba(255, 107, 107, 0.2)',
                      border: '1px solid rgba(255, 107, 107, 0.5)',
                      borderRadius: '5px',
                      color: '#fca5a5',
                      cursor: 'pointer',
                      padding: '3px 8px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      fontFamily: 'var(--font-mono, monospace)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}
                    title="Set Out-Point at current playhead"
                  >
                    <span>OUT ]</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleToggleMute}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: isMuted ? '#f87171' : 'rgba(255, 255, 255, 0.8)',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  </button>

                  <button
                    type="button"
                    onClick={handleToggleFullscreen}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.8)',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                  >
                    {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* TIMELINE TRACK WITH INTERACTIVE HANDLES */}
        <div>
          <div
            ref={trackRef}
            onMouseDown={handleMouseDownTrack}
            style={{
              position: 'relative',
              marginTop: '1.25rem',
              width: '100%',
              height: '70px',
              background: '#0a0d22',
              borderRadius: 'var(--radius-md, 8px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              cursor: previewUrl ? 'pointer' : 'default',
              userSelect: 'none',
              overflow: 'visible',
              opacity: previewUrl ? 1 : 0.6
            }}
          >
            {/* Waveform Bars */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 8px',
                pointerEvents: 'none',
                overflow: 'hidden',
                borderRadius: 'inherit'
              }}
            >
              {waveformBars.map((bar, idx) => (
                <div
                  key={idx}
                  style={{
                    width: '3px',
                    height: `${bar.heightPercent}%`,
                    borderRadius: '2px',
                    background: bar.isInsideTrim
                      ? 'var(--accent-coral, #ff6b6b)'
                      : bar.isPast
                      ? 'var(--accent-purple, #8b5cf6)'
                      : 'rgba(255, 255, 255, 0.14)',
                    transition: 'background 80ms ease'
                  }}
                />
              ))}
            </div>

            {/* Selected In/Out Range Highlight Shading */}
            {showHandles && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${inPointPercent}%`,
                  width: `${Math.max(0, outPointPercent - inPointPercent)}%`,
                  background: 'rgba(255, 107, 107, 0.22)',
                  borderTop: '2px solid rgba(255, 107, 107, 0.7)',
                  borderBottom: '2px solid rgba(255, 107, 107, 0.7)',
                  pointerEvents: 'none'
                }}
              />
            )}

            {/* IN-POINT HANDLE (◀ Left Gripper) */}
            {showHandles && (
              <div
                onMouseDown={(e) => handleStartDrag('in', e)}
                style={{
                  position: 'absolute',
                  top: '-4px',
                  bottom: '-4px',
                  left: `${inPointPercent}%`,
                  width: '18px',
                  transform: 'translateX(-100%)',
                  background: 'linear-gradient(135deg, #e11d48 0%, #ff6b6b 100%)',
                  borderRadius: '6px 0 0 6px',
                  cursor: 'ew-resize',
                  zIndex: 35,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px',
                  boxShadow: '-3px 0 12px rgba(255, 107, 107, 0.7)',
                  userSelect: 'none'
                }}
                title={`Drag In-Point: ${formatTime(inPoint)} (Min 5s, Max 60s span)`}
              >
                <div style={{ width: '2px', height: '16px', background: 'rgba(255, 255, 255, 0.9)', borderRadius: '1px' }} />
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    marginBottom: '6px',
                    background: '#e11d48',
                    color: '#fff',
                    fontSize: '10px',
                    fontWeight: 800,
                    padding: '2px 5px',
                    borderRadius: '4px',
                    fontFamily: 'var(--font-mono, monospace)',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none'
                  }}
                >
                  IN {formatTime(inPoint)}
                </div>
              </div>
            )}

            {/* OUT-POINT HANDLE (▶ Right Gripper) */}
            {showHandles && (
              <div
                onMouseDown={(e) => handleStartDrag('out', e)}
                style={{
                  position: 'absolute',
                  top: '-4px',
                  bottom: '-4px',
                  left: `${outPointPercent}%`,
                  width: '18px',
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #e11d48 100%)',
                  borderRadius: '0 6px 6px 0',
                  cursor: 'ew-resize',
                  zIndex: 35,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px',
                  boxShadow: '3px 0 12px rgba(255, 107, 107, 0.7)',
                  userSelect: 'none'
                }}
                title={`Drag Out-Point: ${formatTime(outPoint)} (Min 5s, Max 60s span)`}
              >
                <div style={{ width: '2px', height: '16px', background: 'rgba(255, 255, 255, 0.9)', borderRadius: '1px' }} />
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    marginBottom: '6px',
                    background: '#e11d48',
                    color: '#fff',
                    fontSize: '10px',
                    fontWeight: 800,
                    padding: '2px 5px',
                    borderRadius: '4px',
                    fontFamily: 'var(--font-mono, monospace)',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none'
                  }}
                >
                  OUT {formatTime(outPoint)}
                </div>
              </div>
            )}

            {/* REAL-TIME PLAYHEAD */}
            <div
              style={{
                position: 'absolute',
                top: '-6px',
                bottom: '-6px',
                left: `${playheadPercent}%`,
                width: '3px',
                background: '#ffffff',
                boxShadow: '0 0 10px #ffffff, 0 0 18px #a855f7',
                zIndex: 40,
                pointerEvents: 'none',
                transform: 'translateX(-50%)'
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '-4px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '8px',
                  height: '8px',
                  background: '#ffffff',
                  borderRadius: '50%',
                  boxShadow: '0 0 6px #ffffff'
                }}
              />
            </div>
          </div>

          {/* Timeline Information & Span Indicator */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.6rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Span: <strong style={{ color: isSpanValid ? '#ffffff' : '#f87171' }}>{totalSpan.toFixed(1)}s</strong></span>
              {!isSpanValid && (
                <span style={{ color: '#f87171' }}>
                  (Allowed: 5.0s – 60.0s)
                </span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                type="button"
                onClick={() => {
                  if (isZoomed) {
                    setIsZoomed(false);
                    setZoomBounds(null);
                  } else {
                    const span = outPoint - inPoint;
                    const pad = Math.max(1.0, Math.min(5.0, span * 0.2));
                    setZoomBounds({
                      start: Math.max(0, Math.round((inPoint - pad) * 10) / 10),
                      end: Math.min(safeDuration, Math.round((outPoint + pad) * 10) / 10)
                    });
                    setIsZoomed(true);
                  }
                }}
                disabled={!previewUrl}
                style={{
                  background: isZoomed ? 'rgba(168, 85, 247, 0.2)' : 'none',
                  border: `1px solid ${isZoomed ? 'rgba(168, 85, 247, 0.5)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '4px',
                  color: isZoomed ? '#c084fc' : 'var(--text-muted)',
                  cursor: previewUrl ? 'pointer' : 'default',
                  padding: '2px 6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.74rem'
                }}
                title={isZoomed ? 'Show entire video on timeline' : 'Zoom in on the selected range'}
              >
                {isZoomed ? <ZoomOut size={12} /> : <ZoomIn size={12} />}
                <span>{isZoomed ? 'Full View' : 'Zoom'}</span>
              </button>

              <button
                type="button"
                onClick={handleReset}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.76rem'
                }}
              >
                <RotateCcw size={12} />
                <span>Reset Range</span>
              </button>

              <button
                type="button"
                onClick={() => setIsLooping(!isLooping)}
                style={{
                  background: isLooping ? 'rgba(168, 85, 247, 0.2)' : 'none',
                  border: `1px solid ${isLooping ? 'rgba(168, 85, 247, 0.5)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '4px',
                  color: isLooping ? '#c084fc' : 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '2px 6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.74rem'
                }}
              >
                <Repeat size={12} />
                <span>Loop {isLooping ? 'ON' : 'OFF'}</span>
              </button>
            </div>
          </div>

          {/* Legal Rights Warranty Banner (Crucial DMCA Safe Harbor Requirement) */}
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: 'var(--radius-md, 8px)',
            background: 'rgba(255, 255, 255, 0.03)',
            border: rightsAffirmed ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(255, 255, 255, 0.12)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            transition: 'border 180ms ease'
          }}>
            <input
              type="checkbox"
              id="rights-warranty-affirmation"
              checked={rightsAffirmed}
              onChange={(e) => setRightsAffirmed(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: 'var(--accent-purple, #a855f7)',
                flexShrink: 0
              }}
            />
            <label
              htmlFor="rights-warranty-affirmation"
              style={{
                fontSize: '0.82rem',
                color: rightsAffirmed ? '#e2e8f0' : 'var(--text-muted)',
                cursor: 'pointer',
                lineHeight: 1.4,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <ShieldCheck size={16} color={rightsAffirmed ? '#34d399' : '#94a3b8'} style={{ flexShrink: 0 }} />
              <span>
                <strong>Rights Affirmation:</strong> I affirm that I own this media or have permission/license to use, dub, and share it under SoulSync terms.
              </span>
            </label>
          </div>

          {/* Action Row: Play Controls, Naming, and SoulCapture */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '1.25rem',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            {/* Play/Review Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                type="button"
                onClick={handleTogglePlay}
                disabled={!previewUrl}
                className="btn-secondary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.85rem'
                }}
              >
                {isPlaying && !isReviewing ? <Pause size={16} /> : <Play size={16} />}
                <span>{isPlaying && !isReviewing ? 'Pause Video' : 'Play Video'}</span>
              </button>

              <button
                type="button"
                onClick={handleToggleReview}
                disabled={!previewUrl || !isSpanValid}
                className="btn-secondary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.85rem',
                  background: isReviewing ? 'rgba(168, 85, 247, 0.25)' : undefined,
                  borderColor: isReviewing ? 'var(--accent-purple, #a855f7)' : undefined
                }}
              >
                {isReviewing && isPlaying ? <Pause size={16} /> : <Play size={16} />}
                <span>{isReviewing && isPlaying ? 'Pause Review' : 'Review Clip'}</span>
              </button>
            </div>

            {/* Clip Naming & SoulCapture Trigger */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <input
                type="text"
                placeholder="Name your clip..."
                value={clipName}
                onChange={(e) => setClipName(e.target.value)}
                style={{
                  height: '38px',
                  padding: '0 12px',
                  fontSize: '0.84rem',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: 'var(--radius-md, 8px)',
                  color: '#ffffff',
                  outline: 'none',
                  width: '160px'
                }}
              />

              <button
                type="button"
                disabled={!selectedFile || !rightsAffirmed || !isSpanValid || !clipName.trim()}
                onClick={handleUploadAndCapture}
                className="btn-primary"
                style={{
                  height: '38px',
                  padding: '0 1.25rem',
                  fontSize: '0.88rem',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (!selectedFile || !rightsAffirmed || !isSpanValid || !clipName.trim()) ? 0.6 : 1,
                  cursor: (!selectedFile || !rightsAffirmed || !isSpanValid || !clipName.trim()) ? 'not-allowed' : 'pointer'
                }}
                title={
                  !selectedFile
                    ? 'Please upload a video file first'
                    : !rightsAffirmed
                    ? 'Please check the rights affirmation box to proceed'
                    : !isSpanValid
                    ? 'Clip length must be between 5s and 60s'
                    : !clipName.trim()
                    ? 'Please name your clip'
                    : 'SoulCapture: Open in Dub Studio to stream & reprocess'
                }
              >
                <Film size={16} />
                <span>SoulCapture</span>
                <span
                  style={{
                    fontSize: '0.66rem',
                    fontWeight: 800,
                    padding: '1px 5px',
                    borderRadius: '4px',
                    background: isPro ? 'rgba(234, 179, 8, 0.35)' : 'rgba(0, 0, 0, 0.35)',
                    border: isPro ? '1px solid rgba(234, 179, 8, 0.7)' : '1px solid rgba(255, 255, 255, 0.25)',
                    color: isPro ? '#fef08a' : '#f3e8ff'
                  }}
                >
                  {isPro ? 'PRO' : '1 Tkn'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
